import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const stats = [
  { value: '20+', label: 'Years of Experience' },
  { value: '$500M+', label: 'In Real Estate Sales' },
  { value: '500+', label: 'Clients Represented' },
  { value: '5 ★', label: 'Client Satisfaction' },
];

const values = [
  { icon: 'ri-shield-check-line', title: 'Integrity First', text: 'Every transaction is handled with absolute transparency, honesty, and discretion — from initial consultation through final closing in the Lake Tahoe market.' },
  { icon: 'ri-lightbulb-line', title: 'Market Intelligence', text: 'Deeply informed by seasonal demand, resort market dynamics, and long-term value trends across Lake Tahoe and the Sierra Nevada.' },
  { icon: 'ri-user-heart-line', title: 'Client-First Philosophy', text: 'Your goals drive every decision. Cam provides personalized guidance that puts your interests at the center of every strategy.' },
  { icon: 'ri-award-line', title: 'Proven Excellence', text: 'A track record of successful high-value transactions in the Lake Tahoe luxury real estate market.' },
];

const reasons = [
  { icon: 'ri-map-pin-2-line', title: 'Local Lake Tahoe Expertise', text: 'Deep knowledge of resort dynamics and seasonal demand.' },
  { icon: 'ri-scales-3-line', title: 'Precision Pricing', text: 'Data-backed analysis for optimal negotiation outcomes.' },
  { icon: 'ri-megaphone-line', title: 'Elevated Marketing', text: 'Sophisticated positioning for luxury buyers and sellers.' },
  { icon: 'ri-vip-crown-line', title: 'Concierge Service', text: 'White-glove, personalized guidance throughout every transaction.' },
];

const testimonials = [
  { quote: 'Cam\'s deep knowledge of the Tahoe market made all the difference. Professional, discreet, and results-driven from day one.', author: 'M. Langford', location: 'South Lake Tahoe, CA', stars: 5 },
  { quote: 'We sold our Lake Tahoe property above asking. Cam\'s pricing strategy and marketing were exceptional.', author: 'T. & A. Russell', location: 'South Lake Tahoe, CA', stars: 5 },
  { quote: 'Finding our second home was effortless with Cam\'s guidance. Knew every property in the area and matched us perfectly.', author: 'C. Beaumont', location: 'Alpine County, CA', stars: 5 },
];

const faqs = [
  { q: 'How long has Cam Kirkwood been in real estate?', a: 'Cam Kirkwood has been a luxury real estate specialist in the Lake Tahoe market for over 20 years, representing buyers, sellers, and investors through hundreds of high-value alpine property transactions across the Sierra Nevada region.' },
  { q: 'What makes Cam Kirkwood different from other agents?', a: 'Cam combines deep local market expertise with a concierge-level client approach, precision pricing strategy, and elevated marketing — all backed by 20+ years of Lake Tahoe and Sierra Nevada real estate experience. Every client receives a fully personalized strategy.' },
  { q: 'Does Cam Kirkwood work with both buyers and sellers?', a: 'Yes. Cam provides full-service representation for both buyers and sellers in South Lake Tahoe, Alpine County, El Dorado County, and surrounding Sierra Nevada communities — from luxury home sales and buyer representation to vacation properties and investment advisory.' },
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="font-sans bg-warm-white overflow-x-hidden">
      <Header />

      {/* 1. HERO */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/b15818af8e7fc7bab34cc5c03d1345a9.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/75 via-charcoal/55 to-charcoal/80" />
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-warm-white to-transparent z-10" />
        <div className="relative z-20 max-w-4xl mx-auto text-center px-4 md:px-8 pt-28 pb-16">
          <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-5">About</p>
          <h1 className="font-serif text-5xl md:text-7xl text-white font-light leading-tight mb-6">
            About <em className="italic text-gold">Cam Kirkwood</em>
          </h1>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gold/40" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold/50" />
            <div className="w-12 h-px bg-gold/40" />
          </div>
          <p className="text-warm-white/65 max-w-xl mx-auto text-base leading-relaxed">
            Experience in Tahoe real estate &bull; Local expertise &bull; Client-first approach &bull; Market knowledge
          </p>
        </div>
      </section>

      {/* 2. TRUST BAR */}
      <section className="bg-charcoal border-y border-gold/15 py-5 overflow-hidden">
        <div className="flex items-center trust-marquee-about">
          {['20+ Years Experience', '$500M+ In Sales', '500+ Clients Served', '★★★★★ Rated', 'Compass Florida, LLC', 'Lake Tahoe & Sierra Nevada Specialist', '20+ Years Experience', '$500M+ In Sales', '500+ Clients Served', '★★★★★ Rated', 'Compass Florida, LLC', 'Lake Tahoe & Sierra Nevada Specialist'].map((item, i) => (
            <span key={i} className="flex items-center gap-6 whitespace-nowrap text-warm-white/60 text-[10px] tracking-[0.25em] uppercase font-light px-8">
              {item}<span className="text-gold/40 text-xs">◆</span>
            </span>
          ))}
        </div>
        <style>{`.trust-marquee-about { animation: marquee 30s linear infinite; } @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
      </section>

      {/* 3. BIO */}
      <section className="py-24 px-4 md:px-8 bg-warm-white rounded-t-[2rem] -mt-8 relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
          {/* Photo */}
          <div className="lg:w-2/5">
            <div className="relative">
              <div className="w-full h-[560px] rounded-2xl overflow-hidden" style={{ boxShadow: '0 12px 48px rgba(42,42,42,0.12)' }}>
                <img
                  src="https://www.camkirkwood.com/images/cam-kirkwood.jpg"
                  alt="Cam Kirkwood – Luxury Real Estate Specialist in Kirkwood and Lake Tahoe"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 w-full h-full border border-gold/20 rounded-2xl -z-10" />
              {/* Badge */}
              <div className="absolute bottom-8 left-8 bg-white/95 px-5 py-4 rounded-xl" style={{ boxShadow: '0 4px 20px rgba(42,42,42,0.1)' }}>
                <img src="https://www.camkirkwood.com/images/logo-compass_w344.png" alt="Compass Real Estate" className="h-5 object-contain opacity-80" />
                <p className="text-[9px] tracking-[0.2em] uppercase text-stone mt-1">Compass Florida, LLC</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:w-3/5">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Biography</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light leading-tight mb-6">
              Your Lake Tahoe &amp; Sierra Nevada <em className="italic">Real Estate Expert</em>
            </h2>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-gold/50" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold/60" />
              <div className="w-12 h-px bg-gold/50" />
            </div>
            <div className="space-y-4 text-stone text-sm leading-relaxed">
              <p>
                Cam Kirkwood is a distinguished luxury real estate professional with a career defined by precision, local expertise, and an unwavering commitment to client success. Specializing in the Lake Tahoe and Sierra Nevada markets, Cam brings an unmatched understanding of alpine resort real estate — from seasonal demand patterns to the nuances of lakefront and ski-access property valuation.
              </p>
              <p>
                With over two decades of experience in high-value real estate transactions, Cam has represented buyers and sellers across the full spectrum of luxury properties — from intimate mountain retreats to expansive lakefront estates. Every client relationship is built on transparency, discretion, and a results-driven approach that consistently delivers outcomes beyond expectations.
              </p>
              <p>
                Cam&apos;s deep roots in the Tahoe region provide clients with an insider&apos;s perspective on emerging opportunities, off-market listings, and the long-term investment dynamics that define the Sierra Nevada luxury real estate market.
              </p>
            </div>
            <div className="flex flex-col gap-3 mt-8">
              <a href="tel:5617146589" className="flex items-center gap-3 text-charcoal hover:text-gold transition-colors cursor-pointer text-sm whitespace-nowrap w-fit">
                <div className="w-9 h-9 flex items-center justify-center border border-gold/30 rounded-lg"><i className="ri-phone-line text-gold" /></div>
                561-714-6589
              </a>
              <a href="mailto:cam@camkirkwood.com" className="flex items-center gap-3 text-charcoal hover:text-gold transition-colors cursor-pointer text-sm w-fit">
                <div className="w-9 h-9 flex items-center justify-center border border-gold/30 rounded-lg"><i className="ri-mail-line text-gold" /></div>
                cam@camkirkwood.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="bg-gold py-10 px-4 md:px-8 relative z-20">
        <div className="max-w-4xl mx-auto text-center flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-2xl text-white font-light">Ready to Work With Cam? Let&apos;s Connect.</p>
          <a href="#consultation" className="whitespace-nowrap px-7 py-4 bg-charcoal text-white text-xs tracking-[0.2em] uppercase font-medium cursor-pointer rounded-md hover:bg-charcoal/85 transition-all duration-300">
            Schedule Consultation
          </a>
        </div>
      </section>

      {/* 4. AI BLOCK */}
      <section className="py-24 px-4 md:px-8 bg-charcoal relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Quick Answers</p>
            <h2 className="font-serif text-4xl text-white font-light">About <em className="italic text-gold">Cam Kirkwood</em></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { q: 'What does Cam Kirkwood specialize in?', a: 'Luxury real estate in Kirkwood and Lake Tahoe, with deep expertise in resort properties, buyer representation, luxury home sales, and investment advisory across the Sierra Nevada region.' },
              { q: 'How long has Cam been in real estate?', a: 'Over 20 years of high-value real estate experience in the Kirkwood and Lake Tahoe markets, with $500M+ in completed transactions and 500+ satisfied buyers and sellers.' },
              { q: 'What is Cam\'s client philosophy?', a: 'Client-first, always. Cam provides concierge-level guidance with full transparency, absolute discretion, and results-driven execution tailored to each client\'s unique goals in the Tahoe luxury market.' },
              { q: 'How do I get started with Cam?', a: 'Contact Cam at 561-714-6589 or cam@camkirkwood.com, or use the consultation form. Cam will respond within 24 hours to schedule your private real estate consultation.' },
            ].map((item, i) => (
              <div key={i} className="rounded-xl p-6 border border-white/8 bg-white/3">
                <p className="font-serif text-base text-gold font-medium mb-3">{item.q}</p>
                <p className="text-warm-white/60 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. PROCESS */}
      <section className="py-20 px-4 md:px-8 bg-charcoal border-t border-gold/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-serif text-4xl text-white font-light">The <em className="italic text-gold">Process</em></h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {['Consultation', 'Strategy', 'Execution', 'Closing'].map((step, i) => (
              <div key={step} className="text-center">
                <span className="font-serif text-5xl text-gold/15 block mb-3 font-light">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-warm-white text-sm tracking-wider font-medium">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. WHY CHOOSE US — Stats + Values */}
      <section className="py-24 px-4 md:px-8 bg-warm-white rounded-t-[2rem] -mt-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Performance</p>
            <h2 className="font-serif text-4xl text-charcoal font-light">Proven <em className="italic">Results</em></h2>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
            {stats.map((s) => (
              <div key={s.label} className="bg-charcoal rounded-xl p-6 text-center">
                <p className="font-serif text-4xl text-gold font-light mb-2">{s.value}</p>
                <p className="text-warm-white/50 text-[10px] tracking-[0.15em] uppercase">{s.label}</p>
              </div>
            ))}
          </div>
          {/* Values */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-cream rounded-xl p-6 border border-cream/80">
                <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg mb-5 border border-gold/15">
                  <i className={`${v.icon} text-gold text-xl`} />
                </div>
                <h3 className="font-serif text-lg text-charcoal font-medium mb-2">{v.title}</h3>
                <p className="text-stone text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. SERVICE AREAS */}
      <section className="py-16 px-4 md:px-8 bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-3">Coverage</p>
            <h2 className="font-serif text-3xl text-charcoal font-light">Service <em className="italic">Areas</em></h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {['South Lake Tahoe', 'North Lake Tahoe', 'Alpine County', 'El Dorado County', 'Sierra Nevada'].map((area) => (
              <div key={area} className="bg-white rounded-lg px-5 py-3 border border-cream/80">
                <i className="ri-map-pin-line text-gold mr-2 text-sm" />
                <span className="text-charcoal text-sm font-medium">{area}</span>
              </div>
            ))}
          </div>
          <div className="rounded-2xl overflow-hidden h-64 border border-gold/10">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d51093.23504258905!2d-120.12497282028503!3d38.68189099999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80995413b10f0db5%3A0x5b7b3a0b0b0b0b0b!2sKirkwood%2C%20CA%2095646!5e0!3m2!1sen!2sus!4v1711000000000!5m2!1sen!2sus"
              className="w-full h-full"
              title="Cam Kirkwood Real Estate Service Area — Kirkwood and Lake Tahoe"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIALS */}
      <section className="py-24 px-4 md:px-8 bg-charcoal">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Client Voices</p>
            <h2 className="font-serif text-4xl text-white font-light">What <em className="italic text-gold">Clients Say</em></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-taupe rounded-2xl p-7 border border-white/5">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.stars }).map((_, si) => <i key={si} className="ri-star-fill text-gold text-sm" />)}
                </div>
                <i className="ri-double-quotes-l text-gold/25 text-4xl block mb-3" />
                <p className="text-warm-white/65 text-sm leading-relaxed mb-6 italic">{t.quote}</p>
                <div className="border-t border-white/10 pt-4">
                  <p className="font-serif text-white font-medium">{t.author}</p>
                  <p className="text-gold/60 text-xs mt-0.5">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-24 px-4 md:px-8 bg-warm-white rounded-t-[2rem] -mt-4 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-3">Questions</p>
            <h2 className="font-serif text-4xl text-charcoal font-light">About <em className="italic">Cam Kirkwood</em></h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-cream bg-white">
                <button className="w-full flex justify-between items-start px-6 py-5 text-left cursor-pointer gap-4" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-serif text-lg text-charcoal font-medium leading-snug">{f.q}</span>
                  <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full border mt-0.5 transition-all duration-300 ${openFaq === i ? 'border-gold bg-gold text-white rotate-45' : 'border-stone/25 text-stone'}`}>
                    <i className="ri-add-line text-sm" />
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-400 ${openFaq === i ? 'max-h-48' : 'max-h-0'}`}>
                  <p className="px-6 pb-5 text-stone text-sm leading-relaxed">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section id="consultation" className="relative py-20 px-4 md:px-8 bg-charcoal text-center overflow-hidden">
        <div className="relative z-10 max-w-2xl mx-auto">
          <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Let&apos;s Work Together</p>
          <h2 className="font-serif text-4xl md:text-5xl text-white font-light mb-5">
            Begin Your <em className="italic text-gold">Tahoe Journey</em>
          </h2>
          <p className="text-warm-white/60 mb-10 leading-relaxed">
            Ready to buy or sell in Kirkwood or Lake Tahoe? Let&apos;s connect for a private consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:5617146589" className="whitespace-nowrap px-8 py-4 border border-warm-white/25 text-warm-white text-xs tracking-[0.2em] uppercase cursor-pointer rounded-md hover:border-gold hover:text-gold transition-all duration-300">
              <i className="ri-phone-line mr-2 text-gold" />Call 561-714-6589
            </a>
            <Link to="/#consultation" className="whitespace-nowrap px-8 py-4 bg-gold text-white text-xs tracking-[0.2em] uppercase cursor-pointer rounded-md hover:bg-[#b8983a] transition-colors duration-300">
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}